import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { SendIcon, MessageCircleIcon, PhoneIcon, MailIcon, CheckCircleIcon, ArrowRightIcon } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import { useTranslations } from '../i18n/translations';
interface FormData {
  name: string;
  company: string;
  phone: string;
  email: string;
}
interface FormErrors {
  name?: string;
  company?: string;
  phone?: string;
  email?: string;
}
interface UtmParams {
  utm_source: string;
  utm_medium: string;
  utm_campaign: string;
  utm_term: string;
  utm_content: string;
}
function getUtmParams(): UtmParams {
  const params = new URLSearchParams(window.location.search);
  return {
    utm_source: params.get('utm_source') || sessionStorage.getItem('utm_source') || '',
    utm_medium: params.get('utm_medium') || sessionStorage.getItem('utm_medium') || '',
    utm_campaign: params.get('utm_campaign') || sessionStorage.getItem('utm_campaign') || '',
    utm_term: params.get('utm_term') || sessionStorage.getItem('utm_term') || '',
    utm_content: params.get('utm_content') || sessionStorage.getItem('utm_content') || ''
  };
}
export function ContactForm() {
  const {
    lang
  } = useLanguage();
  const t = useTranslations(lang);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    company: '',
    phone: '',
    email: ''
  });
  const [utmParams, setUtmParams] = useState<UtmParams>({
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: ''
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  useEffect(() => {
    const utms = getUtmParams();
    setUtmParams(utms);
    // Сохраняем в sessionStorage чтобы не потерять при переходе между страницами
    Object.entries(utms).forEach(([key, value]) => {
      if (value) sessionStorage.setItem(key, value);
    });
  }, []);
  const validate = (): boolean => {
    const newErrors: FormErrors = {};
    if (!formData.name.trim()) newErrors.name = t.contact.errors.name;
    if (!formData.company.trim()) newErrors.company = t.contact.errors.company;
    if (!formData.phone.trim()) {
      newErrors.phone = t.contact.errors.phone_required;
    } else if (!/^[+\d\s()-]{7,}$/.test(formData.phone)) {
      newErrors.phone = t.contact.errors.phone_invalid;
    }
    if (!formData.email.trim()) {
      newErrors.email = t.contact.errors.email_required;
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t.contact.errors.email_invalid;
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const response = await fetch('https://formspree.io/f/mdalrzke', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          name: formData.name,
          company: formData.company,
          phone: formData.phone,
          email: formData.email,
          // UTM-метки
          ...utmParams
        })
      });
      if (response.ok) {
        setIsSubmitted(true);
      } else {
        const data = await response.json();
        console.error('Formspree error:', data);
      }
    } catch (err) {
      console.error('Network error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };
  const handleChange = (field: keyof FormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData(prev => ({
      ...prev,
      [field]: e.target.value
    }));
    if (errors[field]) setErrors(prev => ({
      ...prev,
      [field]: undefined
    }));
  };
  return <section className="relative py-20 lg:py-28 bg-white overflow-hidden" id="contact" data-id="element-130">
      <div className="absolute inset-0 pointer-events-none" data-id="element-131">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] rounded-full bg-primary-50/30 blur-3xl hidden sm:block" data-id="element-132" />
        <div className="absolute bottom-0 left-0 w-[300px] h-[300px] rounded-full bg-cyan-50/20 blur-3xl hidden sm:block" data-id="element-133" />
      </div>

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" ref={ref} data-id="element-134">
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20" data-id="element-135">
          {/* Left: info */}
          <motion.div initial={{
          opacity: 0,
          x: -30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6
        }} data-id="element-136">
            <span className="text-sm font-semibold text-primary-600 tracking-wide uppercase" data-id="element-137">
              {t.contact.eyebrow}
            </span>
            <h2 className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight" data-id="element-138">
              {t.contact.title}
            </h2>
            <p className="mt-4 text-lg text-navy-500 leading-relaxed" data-id="element-139">
              {t.contact.subtitle}
            </p>

            <div className="mt-10 space-y-5" data-id="element-140">
              <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-navy-50/80 to-transparent border border-navy-100/50" data-id="element-141">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center shadow-lg shadow-primary-500/15" data-id="element-142">
                  <PhoneIcon className="w-5 h-5" data-id="element-143" />
                </div>
                <div data-id="element-144">
                  <div className="text-xs text-navy-400 font-semibold uppercase tracking-wide" data-id="element-145">
                    {t.contact.phone_label}
                  </div>
                  <div className="flex flex-col" data-id="element-146">
                    <a href="tel:+380668356279" className="text-navy-900 font-semibold hover:text-primary-600 transition-colors" data-id="element-147">
                      +380 66 835 62 79
                    </a>
                    <a href="tel:+380960465744" className="text-navy-900 font-semibold hover:text-primary-600 transition-colors" data-id="element-148">
                      +380 96 046 57 44
                    </a>
                  </div>
                </div>
              </div>

              <div className="flex items-center gap-4 p-4 rounded-xl bg-gradient-to-r from-navy-50/80 to-transparent border border-navy-100/50" data-id="element-149">
                <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 text-white flex items-center justify-center shadow-lg shadow-primary-500/15" data-id="element-150">
                  <MailIcon className="w-5 h-5" data-id="element-151" />
                </div>
                <div data-id="element-152">
                  <div className="text-xs text-navy-400 font-semibold uppercase tracking-wide" data-id="element-153">
                    {t.contact.email_label}
                  </div>
                  <a href="mailto:sales@e-s-c-a.com" className="text-navy-900 font-semibold hover:text-primary-600 transition-colors" data-id="element-154">
                    sales@e-s-c-a.com
                  </a>
                </div>
              </div>
            </div>

            <div className="mt-10" data-id="element-155">
              <p className="text-sm font-semibold text-navy-500 mb-4" data-id="element-156">
                {t.contact.messenger_label}
              </p>
              <div className="flex flex-wrap gap-3" data-id="element-157">
                <a href="https://t.me/sergeydenisyuk" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#229ED9]/10 text-[#229ED9] font-semibold text-sm hover:bg-[#229ED9]/20 transition-colors border border-[#229ED9]/15" data-id="element-158">
                  <MessageCircleIcon className="w-5 h-5" data-id="element-159" />
                  Telegram
                </a>
                <a href="viber://chat?number=380668356279" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#7360F2]/10 text-[#7360F2] font-semibold text-sm hover:bg-[#7360F2]/20 transition-colors border border-[#7360F2]/15" data-id="element-160">
                  <MessageCircleIcon className="w-5 h-5" data-id="element-161" />
                  Viber
                </a>
                <a href="https://wa.me/380668356279" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-[#25D366]/10 text-[#25D366] font-semibold text-sm hover:bg-[#25D366]/20 transition-colors border border-[#25D366]/15" data-id="element-162">
                  <MessageCircleIcon className="w-5 h-5" data-id="element-163" />
                  WhatsApp
                </a>
              </div>
            </div>

            <motion.div initial={{
            opacity: 0,
            y: 16
          }} animate={isInView ? {
            opacity: 1,
            y: 0
          } : {}} transition={{
            duration: 0.5,
            delay: 0.4
          }} className="mt-10 flex items-center gap-3 p-4 rounded-xl bg-emerald-50/60 border border-emerald-100" data-id="element-164">
              <CheckCircleIcon className="w-5 h-5 text-emerald-600 flex-shrink-0" data-id="element-165" />
              <p className="text-sm text-emerald-700 font-medium" data-id="element-166">
                {t.contact.trust_badge}
              </p>
            </motion.div>
          </motion.div>

          {/* Right: form */}
          <motion.div initial={{
          opacity: 0,
          x: 30
        }} animate={isInView ? {
          opacity: 1,
          x: 0
        } : {}} transition={{
          duration: 0.6,
          delay: 0.1
        }} data-id="element-167">
            {isSubmitted ? <div className="flex flex-col items-center justify-center h-full text-center p-8 rounded-2xl bg-gradient-to-b from-white to-emerald-50/30 border border-emerald-100 shadow-lg shadow-emerald-100/20" data-id="element-168">
                <div className="w-16 h-16 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 text-white flex items-center justify-center mb-6 shadow-lg shadow-emerald-500/20" data-id="element-169">
                  <CheckCircleIcon className="w-8 h-8" data-id="element-170" />
                </div>
                <h3 className="text-2xl font-bold text-navy-900 mb-2" data-id="element-171">
                  {t.contact.success_title}
                </h3>
                <p className="text-navy-500" data-id="element-172">{t.contact.success_subtitle}</p>
                <button onClick={() => {
              setIsSubmitted(false);
              setFormData({
                name: '',
                company: '',
                phone: '',
                email: ''
              });
            }} className="mt-6 text-primary-600 font-semibold hover:text-primary-700 transition-colors" data-id="element-173">
                  {t.contact.send_another}
                </button>
              </div> : <form onSubmit={handleSubmit} className="p-5 sm:p-8 lg:p-10 rounded-2xl bg-gradient-to-b from-white to-navy-50/40 border border-navy-200/60 shadow-xl shadow-navy-200/20" noValidate data-id="element-174">
                <h3 className="text-lg font-bold text-navy-900 mb-6" data-id="element-175">
                  {t.contact.form_title}
                </h3>
                <div className="space-y-5" data-id="element-176">
                  <div data-id="element-177">
                    <label htmlFor="name" className="block text-sm font-semibold text-navy-700 mb-1.5" data-id="element-178">
                      {t.contact.name_label}
                    </label>
                    <input id="name" type="text" value={formData.name} onChange={handleChange('name')} placeholder={t.contact.name_placeholder} className={`w-full px-4 py-3.5 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 shadow-sm ${errors.name ? 'border-red-400' : 'border-navy-200'}`} data-id="element-179" />
                    {errors.name && <p className="mt-1.5 text-sm text-red-500 font-medium" data-id="element-180">
                        {errors.name}
                      </p>}
                  </div>
                  <div data-id="element-181">
                    <label htmlFor="company" className="block text-sm font-semibold text-navy-700 mb-1.5" data-id="element-182">
                      {t.contact.company_label}
                    </label>
                    <input id="company" type="text" value={formData.company} onChange={handleChange('company')} placeholder={t.contact.company_placeholder} className={`w-full px-4 py-3.5 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 shadow-sm ${errors.company ? 'border-red-400' : 'border-navy-200'}`} data-id="element-183" />
                    {errors.company && <p className="mt-1.5 text-sm text-red-500 font-medium" data-id="element-184">
                        {errors.company}
                      </p>}
                  </div>
                  <div data-id="element-185">
                    <label htmlFor="phone" className="block text-sm font-semibold text-navy-700 mb-1.5" data-id="element-186">
                      {t.contact.phone_form_label}
                    </label>
                    <input id="phone" type="tel" value={formData.phone} onChange={handleChange('phone')} placeholder={t.contact.phone_placeholder} className={`w-full px-4 py-3.5 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 shadow-sm ${errors.phone ? 'border-red-400' : 'border-navy-200'}`} data-id="element-187" />
                    {errors.phone && <p className="mt-1.5 text-sm text-red-500 font-medium" data-id="element-188">
                        {errors.phone}
                      </p>}
                  </div>
                  <div data-id="element-189">
                    <label htmlFor="email" className="block text-sm font-semibold text-navy-700 mb-1.5" data-id="element-190">
                      {t.contact.email_form_label}
                    </label>
                    <input id="email" type="email" value={formData.email} onChange={handleChange('email')} placeholder={t.contact.email_placeholder} className={`w-full px-4 py-3.5 rounded-xl border bg-white text-navy-900 placeholder-navy-400 text-sm transition-all focus:outline-none focus:ring-2 focus:ring-primary-500/30 focus:border-primary-500 shadow-sm ${errors.email ? 'border-red-400' : 'border-navy-200'}`} data-id="element-191" />
                    {errors.email && <p className="mt-1.5 text-sm text-red-500 font-medium" data-id="element-192">
                        {errors.email}
                      </p>}
                  </div>
                </div>
                <button type="submit" disabled={isSubmitting} className="mt-7 w-full inline-flex items-center justify-center gap-2 px-6 py-4 text-base font-semibold text-white bg-gradient-to-r from-primary-600 to-primary-500 rounded-xl hover:from-primary-700 hover:to-primary-600 transition-all shadow-lg shadow-primary-600/25 hover:shadow-xl hover:shadow-primary-600/30 disabled:opacity-60 disabled:cursor-not-allowed" data-id="element-193">
                  {isSubmitting ? <>
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" data-id="element-194" />
                      {t.contact.submitting}
                    </> : <>
                      <SendIcon className="w-4 h-4" data-id="element-195" />
                      {t.contact.submit}
                      <ArrowRightIcon className="w-4 h-4" data-id="element-196" />
                    </>}
                </button>
                <p className="mt-4 text-xs text-navy-400 text-center" data-id="element-197">
                  {t.contact.privacy}
                </p>
              </form>}
          </motion.div>
        </div>
      </div>
    </section>;
}